import { AddCircleOutline } from '@mui/icons-material'
import { Box, useTheme } from '@mui/material'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateCustomer } from 'src/api/customer'
import { invalidateLocalityCache } from 'src/api/localities'
import { CommonPageLayout } from 'src/components/common/CommonPageLayout'
import { useDialogCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'
import {
  invalidateLocalityGroupsQueries,
  localityGroupsEndpoints,
  useDeleteLocalityGroup,
} from '../../../api/locality-groups'
import { ILocalityGroup } from '../../../generated-types'
import { ResourceScreenProps } from '../../../generated-types/resource-screen'
import { useCustomerCtx } from '../../../hooks/context-hooks'
import { CreateLocalityGroupDialog } from './CreateLocalityGroupDialog'
import LocalityGroupDetailPanel from './LocalityGroupDetailPanel'
import { LocalityGroupsTable } from './LocalityGroupsTable'
import UpdateLocalityGroup from './UpdateLocalityGroup'

export const LocalityGroupsScreen: FC<ResourceScreenProps> = ({
  renderTableExpand,
  query,
  params,
  generateQueryKey,
  handleUpdate,
  handleDelete,
  handleAdd,
  isActionAvailable = true,
  isTableExpandable = true,
}) => {
  const { customer } = useCustomerCtx()
  const { t } = useTranslation(['common', 'access-control', 'common-api-message', 'locality-group'])
  const { showConfirmDialog, showDialog } = useDialogCtx()
  const { mutateAsync: deleteGroup } = useDeleteLocalityGroup(customer?.id)
  const theme = useTheme()

  const onAdd = async () => {
    const added = await showDialog(
      CreateLocalityGroupDialog,
      {
        title: t('create-locality-group', { ns: 'access-control' }),
        opts: {
          maxWidth: 'sm',
          fullWidth: true,
        },
        componentProps: {
          customerId: customer.id as EntityId,
        },
      },
      undefined,
    )
    if (!added) return
  }

  const deleteLocalityGroupAction = async (id: EntityId) => {
    showConfirmDialog(
      t('delete-confirm-locality-group-title', { ns: 'locality-group' }),
      t('delete-confirm-locality-group-description', { ns: 'locality-group' }),
      { acceptText: t('yes', { ns: 'common' }), cancelText: t('no', { ns: 'common' }) },
      async pop => {
        await deleteGroup(id)
        toast.success(t('successfullyRemoved', { ns: 'common-api-message' }))
        invalidateLocalityGroupsQueries.invalidateGetLocalityGroups(customer?.id)
        invalidateLocalityGroupsQueries.invalidateGetLocalityGroupsMinimalData(customer?.id)
        invalidateCustomer.getCustomerUsersForAdmins(customer?.id)
        invalidateLocalityCache.CustomerLocalitiesWithGroups(customer?.id)
        pop()
      },
    )
  }

  const editLocalityGroup = async id => {
    showDialog(UpdateLocalityGroup, {
      componentProps: {
        id: id,
        customerId: customer.id as EntityId,
      },
      ops: {
        maxWidth: 'md',
      },
      title: t('update-locality-group', { ns: 'access-control' }),
    })
  }

  return (
    <CommonPageLayout
      titleSection={{ title: t('locality-groups', { ns: 'locality-group' }) }}
      topAction={[
        {
          buttonText: t('locality-group', { ns: 'page-container' }),
          buttonIcon: <AddCircleOutline />,
          onClick: handleAdd ?? onAdd,
          variant: 'outlined',
          shadowWeight: null,
        },
      ]}
    >
      <Box padding={'8px'} bgcolor={getThemeColor(theme, MUIThemeColors.white)} borderRadius={'4px'}>
        <LocalityGroupsTable
          onUpdate={isActionAvailable ? handleUpdate ?? editLocalityGroup : undefined}
          onDelete={isActionAvailable ? handleDelete ?? deleteLocalityGroupAction : undefined}
          queryUrl={query ?? localityGroupsEndpoints.getLocalityGroups(customer.id)}
          queryParams={params}
          generateQueryKey={generateQueryKey}
          getDetailPanelContent={
            isTableExpandable
              ? renderTableExpand ??
                (params => <LocalityGroupDetailPanel localityGroup={params.row as ILocalityGroup} />)
              : undefined
          }
        />
      </Box>
    </CommonPageLayout>
  )
}
